import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { X, User, UserCheck } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Select, Field, PrimaryButton, SecondaryButton, DangerButton } from "@/components/admin/AdminUI";
import { MultiSelect } from "@/components/public/MultiSelect";
import { toast } from "@/lib/toast";

// Quick presets for the date-range picker.
const RANGES = [
  { key: "7", label: "Last 7 days", days: 7 },
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 90 days", days: 90 },
  { key: "365", label: "Last 12 months", days: 365 },
];

function toIso(d) {
  return d.toISOString().slice(0, 10);
}

export default function AdminAnalytics() {
  const [rangeKey, setRangeKey] = useState("30");
  const [countryCodes, setCountryCodes] = useState([]); // string[] of ISO-2 codes
  const [includeOwner, setIncludeOwner] = useState(false);

  const { start, end } = useMemo(() => {
    const today = new Date();
    const r = RANGES.find((x) => x.key === rangeKey) || RANGES[1];
    const startD = new Date(today);
    startD.setDate(today.getDate() - (r.days - 1));
    return { start: toIso(startD), end: toIso(today) };
  }, [rangeKey]);

  const countryParam = countryCodes.length ? countryCodes.join(",") : undefined;
  const params = { start, end, country: countryParam, include_owner: includeOwner };

  const summary = useQuery({
    queryKey: ["analytics-summary", params],
    queryFn: async () => (await api.get("/admin/analytics/summary", { params })).data,
  });
  const daily = useQuery({
    queryKey: ["analytics-daily", params],
    queryFn: async () => (await api.get("/admin/analytics/daily", { params })).data,
  });
  // Countries chart/table always shows ALL countries (not filtered by the
  // multiselect) so the user can see what they can pick — and the chart still
  // honors include_owner so it's consistent with the rest of the dashboard.
  const countries = useQuery({
    queryKey: ["analytics-countries", { start, end, include_owner: includeOwner }],
    queryFn: async () => (await api.get("/admin/analytics/countries", { params: { start, end, include_owner: includeOwner, limit: 250 } })).data,
  });
  const topPages = useQuery({
    queryKey: ["analytics-top-pages", params],
    queryFn: async () => (await api.get("/admin/analytics/top-pages", { params: { ...params, limit: 15 } })).data,
  });
  const referrers = useQuery({
    queryKey: ["analytics-referrers", params],
    queryFn: async () => (await api.get("/admin/analytics/referrers", { params: { ...params, limit: 20 } })).data,
  });

  const [tab, setTab] = useState("overview"); // 'overview' | 'visitors' | 'outbound'

  // Build country options from whatever the dataset has — sorted by visitor count
  // so the most relevant countries appear first.
  const countryOptions = useMemo(() => (
    (countries.data || [])
      .filter((c) => c.country_code)
      .map((c) => ({
        id: c.country_code,
        label: `${c.country || c.country_code} (${c.country_code}) · ${c.unique_visitors}`,
      }))
  ), [countries.data]);

  return (
    <div data-testid="admin-analytics-page">
      <PageHeader
        title="Analytics"
        subtitle="Public-page visits only. Admin pages are never tracked."
        actions={<div className="flex gap-2"><ClearPreviewButton /><ClearAllButton /></div>}
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#E9ECEF]" data-testid="analytics-tabs">
        <TabButton active={tab === "overview"} onClick={() => setTab("overview")} testid="tab-overview">Overview</TabButton>
        <TabButton active={tab === "visitors"} onClick={() => setTab("visitors")} testid="tab-visitors">Visitors</TabButton>
        <TabButton active={tab === "outbound"} onClick={() => setTab("outbound")} testid="tab-outbound">Outbound clicks</TabButton>
      </div>

      {/* Filters (shared between tabs) */}
      <Card className="mb-6">
        <div className="grid sm:grid-cols-4 gap-3 items-end">
          <Field label="Range">
            <Select value={rangeKey} onChange={(e) => setRangeKey(e.target.value)} data-testid="analytics-range">
              {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </Select>
          </Field>
          <Field label="Country">
            <MultiSelect
              options={countryOptions}
              value={countryCodes}
              onChange={setCountryCodes}
              placeholder="All countries"
              testId="analytics-country-filter"
            />
          </Field>
          <div className="text-xs text-[#6C757D] sm:col-span-2 sm:justify-self-end">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                checked={includeOwner}
                onChange={(e) => setIncludeOwner(e.target.checked)}
                data-testid="analytics-include-owner"
              />
              <span>Include my own visits (default off)</span>
            </label>
          </div>
        </div>
      </Card>

      {tab === "overview" && <OverviewTab summary={summary} daily={daily} countries={countries} topPages={topPages} referrers={referrers} />}
      {tab === "visitors" && <VisitorsTab start={start} end={end} country={countryParam} includeOwner={includeOwner} />}
      {tab === "outbound" && <OutboundTab start={start} end={end} country={countryParam} includeOwner={includeOwner} />}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-sm border-b-2 -mb-px ${active ? "border-[#212529] text-[#212529] font-medium" : "border-transparent text-[#6C757D] hover:text-[#212529]"}`}
    >
      {children}
    </button>
  );
}

function OverviewTab({ summary, daily, countries, topPages, referrers }) {
  return (
    <>
      {/* KPIs */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi label="Unique visitors" value={summary.data?.unique_visitors} testid="kpi-uv" />
        <Kpi label="Pageviews" value={summary.data?.total_pageviews} testid="kpi-pv" />
        <Kpi label="Sessions" value={summary.data?.unique_sessions} testid="kpi-sessions" />
        <Kpi label="Avg pageviews / visitor" value={summary.data?.avg_pageviews_per_visitor} testid="kpi-avg" />
      </div>

      {/* Visitors per day */}
      <Card className="mb-6">
        <h2 className="font-semibold mb-3">Unique visitors per day</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={daily.data || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="unique_visitors" stroke="#1E5631" strokeWidth={2} dot={false} name="Visitors" />
              <Line type="monotone" dataKey="pageviews" stroke="#9CA3AF" strokeWidth={1.5} dot={false} name="Pageviews" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="mb-6">
        {/* Countries — full-width, vertical bars (country names on X axis) */}
        <Card>
          <h2 className="font-semibold mb-3">Top countries</h2>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(countries.data || []).slice(0, 20)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                <XAxis dataKey="country" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="unique_visitors" fill="#1E5631" name="Visitors" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 max-h-48 overflow-y-auto">
            <table className="w-full text-xs" data-testid="countries-table">
              <thead><tr className="text-left text-[#6C757D]"><th>Country</th><th className="text-right">Visitors</th><th className="text-right">Views</th></tr></thead>
              <tbody>
                {(countries.data || []).map((c) => (
                  <tr key={c.country_code || "unknown"} className="border-t border-[#E9ECEF]">
                    <td className="py-1.5">{c.country || "Unknown"} <span className="text-[#6C757D]">{c.country_code || ""}</span></td>
                    <td className="py-1.5 text-right">{c.unique_visitors}</td>
                    <td className="py-1.5 text-right">{c.pageviews}</td>
                  </tr>
                ))}
                {!countries.data?.length && <tr><td colSpan={3} className="text-[#6C757D] py-3 text-center">No data yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Top pages */}
      <Card className="mb-6">
        <h2 className="font-semibold mb-3">Top pages</h2>
        <table className="w-full text-sm" data-testid="top-pages-table">
          <thead><tr className="text-left text-[#6C757D] text-xs"><th>Path</th><th className="text-right">Visitors</th><th className="text-right">Views</th></tr></thead>
          <tbody>
            {(topPages.data || []).map((p) => (
              <tr key={p.path} className="border-t border-[#E9ECEF]">
                <td className="py-2 truncate max-w-[600px]">{p.path}</td>
                <td className="py-2 text-right">{p.unique_visitors}</td>
                <td className="py-2 text-right">{p.pageviews}</td>
              </tr>
            ))}
            {!topPages.data?.length && <tr><td colSpan={3} className="text-[#6C757D] py-3 text-center">No data yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* Referrers */}
      <Card>
        <h2 className="font-semibold mb-3">Traffic sources</h2>
        <p className="text-xs text-[#6C757D] mb-3">Where visitors come from. "Direct" includes typed URLs, bookmarks, app links, and same-site navigation.</p>
        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(referrers.data || []).slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="source" tick={{ fontSize: 11 }} width={110} />
                <Tooltip />
                <Bar dataKey="unique_visitors" fill="#7B61FF" name="Visitors" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <table className="w-full text-sm" data-testid="referrers-table">
            <thead><tr className="text-left text-[#6C757D] text-xs"><th>Source</th><th className="text-right">Visitors</th><th className="text-right">Views</th></tr></thead>
            <tbody>
              {(referrers.data || []).map((r) => (
                <tr key={r.source} className="border-t border-[#E9ECEF]">
                  <td className="py-2">{r.source}</td>
                  <td className="py-2 text-right">{r.unique_visitors}</td>
                  <td className="py-2 text-right">{r.pageviews}</td>
                </tr>
              ))}
              {!referrers.data?.length && <tr><td colSpan={3} className="text-[#6C757D] py-3 text-center">No data yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function VisitorsTab({ start, end, country, includeOwner }) {
  const qc = useQueryClient();
  const [openVid, setOpenVid] = useState(null);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const list = useQuery({
    queryKey: ["analytics-visitors", { start, end, country, includeOwner, page }],
    queryFn: async () => (
      await api.get("/admin/analytics/visitors", {
        params: { start, end, country: country || undefined, include_owner: true, limit: PAGE_SIZE, skip: page * PAGE_SIZE },
      })
    ).data,
  });

  // Show owner flag filter: visitors are always queried with include_owner=true
  // (so you can see and toggle them), but a local "Hide marked-as-me" hides
  // already-flagged rows when filtering the table. The `includeOwner` prop
  // from the page-level filters is reused so users have one control to think about.
  const items = (list.data?.items || []).filter((v) => includeOwner ? true : !v.is_owner);

  async function markOwner(visitorId, next) {
    try {
      await api.post(`/admin/analytics/visitors/${encodeURIComponent(visitorId)}/mark-owner`, { is_owner: next });
      toast.success(next ? "Marked as you — excluded from analytics" : "Unmarked");
      qc.invalidateQueries({ queryKey: ["analytics-visitors"] });
      qc.invalidateQueries({ queryKey: ["analytics-summary"] });
      qc.invalidateQueries({ queryKey: ["analytics-daily"] });
      qc.invalidateQueries({ queryKey: ["analytics-countries"] });
      qc.invalidateQueries({ queryKey: ["analytics-top-pages"] });
      qc.invalidateQueries({ queryKey: ["analytics-referrers"] });
      qc.invalidateQueries({ queryKey: ["analytics-distribution"] });
    } catch (e) {
      toast.error("Failed to update");
    }
  }

  return (
    <Card data-testid="visitors-tab">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="font-semibold">Visitors</h2>
          <p className="text-xs text-[#6C757D]">{list.data?.total ?? "—"} unique visitors in range. Click a row for full activity.</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#6C757D] text-xs">
              <th className="py-2">Visitor</th>
              <th className="py-2">Last seen</th>
              <th className="py-2">Country</th>
              <th className="py-2">Device</th>
              <th className="py-2 text-right">Pageviews</th>
              <th className="py-2 text-right">Sessions</th>
              <th className="py-2 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((v) => (
              <tr
                key={v.visitor_id}
                onClick={() => setOpenVid(v.visitor_id)}
                className="border-t border-[#E9ECEF] hover:bg-[#F8F9FA] cursor-pointer"
                data-testid={`visitor-row-${v.visitor_id}`}
              >
                <td className="py-2 font-mono text-xs">{v.visitor_id.slice(0, 12)}…</td>
                <td className="py-2 text-xs whitespace-nowrap">{fmtRelative(v.last_seen)}</td>
                <td className="py-2 text-xs">{v.last_country || "—"} <span className="text-[#6C757D]">{v.last_country_code || ""}</span></td>
                <td className="py-2 text-xs">{[v.last_browser, v.last_os].filter(Boolean).join(" / ") || "—"}</td>
                <td className="py-2 text-right">{v.pageviews}</td>
                <td className="py-2 text-right">{v.session_count}</td>
                <td className="py-2 text-right">
                  {v.is_owner ? (
                    <span className="inline-flex items-center gap-1 text-[11px] bg-[#212529] text-white px-2 py-0.5 rounded-full"><UserCheck className="w-3 h-3" />Me</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] text-[#6C757D]"><User className="w-3 h-3" />Visitor</span>
                  )}
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={7} className="py-6 text-center text-[#6C757D]">No visitors in this range.</td></tr>}
          </tbody>
        </table>
      </div>
      {list.data?.total > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-3 text-xs text-[#6C757D]">
          <span>Page {page + 1} of {Math.ceil(list.data.total / PAGE_SIZE)}</span>
          <div className="flex gap-2">
            <SecondaryButton onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>Previous</SecondaryButton>
            <SecondaryButton onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * PAGE_SIZE >= list.data.total}>Next</SecondaryButton>
          </div>
        </div>
      )}

      {openVid && <VisitorDetailModal visitorId={openVid} onClose={() => setOpenVid(null)} onMarkOwner={markOwner} />}
    </Card>
  );
}

function VisitorDetailModal({ visitorId, onClose, onMarkOwner }) {
  const { data, isLoading } = useQuery({
    queryKey: ["analytics-visitor-detail", visitorId],
    queryFn: async () => (await api.get(`/admin/analytics/visitors/${encodeURIComponent(visitorId)}`)).data,
  });

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="visitor-detail-modal"
      >
        <div className="px-6 pt-5 pb-3 border-b border-[#E9ECEF]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="font-semibold text-lg">Visitor</h3>
              <code className="text-xs text-[#6C757D] break-all">{visitorId}</code>
            </div>
            <button onClick={onClose} className="text-[#6C757D] hover:text-[#212529]" data-testid="visitor-modal-close"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div className="px-6 py-4 overflow-y-auto space-y-5">
          {isLoading && <div className="text-[#6C757D]">Loading…</div>}
          {data && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                <Stat label="Pageviews" value={data.pageviews} />
                <Stat label="Sessions" value={data.sessions?.length || 0} />
                <Stat label="First seen" value={fmtRelative(data.first_seen)} />
                <Stat label="Last seen" value={fmtRelative(data.last_seen)} />
              </div>

              {data.is_owner ? (
                <div className="flex items-center justify-between bg-[#212529]/5 border border-[#212529]/10 px-3 py-2 rounded">
                  <span className="text-sm inline-flex items-center gap-2"><UserCheck className="w-4 h-4" />Marked as you — excluded from analytics</span>
                  <SecondaryButton onClick={() => onMarkOwner(visitorId, false)} data-testid="visitor-unmark-owner">Unmark</SecondaryButton>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-[#F8F9FA] border border-[#E9ECEF] px-3 py-2 rounded">
                  <span className="text-sm text-[#6C757D]">If this visitor is you, mark it so it's excluded from analytics by default.</span>
                  <PrimaryButton onClick={() => onMarkOwner(visitorId, true)} data-testid="visitor-mark-owner">Mark as me</PrimaryButton>
                </div>
              )}

              <DetailSection title="Countries">
                {data.countries?.length ? (
                  <ul className="text-sm space-y-1">{data.countries.map((c) => (
                    <li key={c.country_code} className="flex justify-between"><span>{c.country} <span className="text-[#6C757D]">{c.country_code}</span></span><span className="text-[#6C757D]">{c.pageviews} views</span></li>
                  ))}</ul>
                ) : <Empty>No country info</Empty>}
              </DetailSection>

              <DetailSection title="Traffic sources">
                {data.referrers?.length ? (
                  <ul className="text-sm space-y-1">{data.referrers.map((r) => (
                    <li key={r.source} className="flex justify-between"><span>{r.source}</span><span className="text-[#6C757D]">{r.pageviews}</span></li>
                  ))}</ul>
                ) : <Empty>No referrer info</Empty>}
              </DetailSection>

              <DetailSection title="Top pages">
                {data.top_pages?.length ? (
                  <ul className="text-sm space-y-1">{data.top_pages.slice(0, 15).map((p) => (
                    <li key={p.path} className="flex justify-between gap-3"><span className="truncate">{p.path}</span><span className="text-[#6C757D]">{p.pageviews}</span></li>
                  ))}</ul>
                ) : <Empty>No paths</Empty>}
              </DetailSection>

              <DetailSection title="Devices">
                {data.devices?.length ? (
                  <ul className="text-sm space-y-1">{data.devices.map((d, i) => (
                    <li key={i}>{[d.browser, d.os].filter(Boolean).join(" / ") || "Unknown"}</li>
                  ))}</ul>
                ) : <Empty>No device info</Empty>}
              </DetailSection>

              <DetailSection title="Sessions">
                {data.sessions?.length ? (
                  <ul className="text-xs space-y-1">{data.sessions.slice(0, 25).map((s) => (
                    <li key={s.session_id} className="flex justify-between">
                      <code className="text-[10px] text-[#6C757D]">{s.session_id.slice(0, 12)}…</code>
                      <span>{s.pageviews} views · {fmtAbs(s.started_at)} → {fmtAbs(s.ended_at)}</span>
                    </li>
                  ))}</ul>
                ) : <Empty>No sessions</Empty>}
              </DetailSection>

              <DetailSection title={`Recent pageviews (${data.recent_pageviews?.length || 0})`}>
                {data.recent_pageviews?.length ? (
                  <ul className="text-xs space-y-1 max-h-64 overflow-y-auto">{data.recent_pageviews.map((pv, i) => (
                    <li key={i} className="flex justify-between gap-3 border-b border-[#F1F3F5] pb-1">
                      <span className="truncate">{pv.path}</span>
                      <span className="text-[#6C757D] whitespace-nowrap">{fmtAbs(pv.created_at)}</span>
                    </li>
                  ))}</ul>
                ) : <Empty>No pageviews</Empty>}
              </DetailSection>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#6C757D]">{label}</div>
      <div className="text-base font-medium">{value}</div>
    </div>
  );
}
function DetailSection({ title, children }) {
  return (
    <section>
      <h4 className="text-xs uppercase tracking-wider text-[#6C757D] mb-2">{title}</h4>
      {children}
    </section>
  );
}
function Empty({ children }) {
  return <div className="text-xs text-[#6C757D]">{children}</div>;
}

function ClearAllButton() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  async function onClick() {
    if (!window.confirm("Delete ALL analytics records?\n\nThis wipes every tracked page-view from the database. Cannot be undone.")) return;
    setBusy(true);
    try {
      const res = await api.delete("/admin/analytics/all");
      toast.success(`Cleared ${res.data?.deleted ?? 0} page-views`);
      // Invalidate every analytics query so the dashboard refreshes immediately.
      qc.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("analytics-") });
    } catch (e) {
      toast.error("Failed to clear analytics");
    } finally {
      setBusy(false);
    }
  }
  return (
    <DangerButton onClick={onClick} disabled={busy} data-testid="analytics-clear-all">
      {busy ? "Clearing…" : "Clear all analytics"}
    </DangerButton>
  );
}

function ClearPreviewButton() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  async function onClick() {
    if (!window.confirm("Delete analytics rows whose referrer points to a preview / agent host (emergentagent.com etc.)?\n\nProduction-domain traffic is NOT touched. Use this to remove leftover noise accumulated before host-filtering was in place.")) return;
    setBusy(true);
    try {
      const res = await api.delete("/admin/analytics/preview-traffic");
      toast.success(`Removed ${res.data?.page_views_deleted ?? 0} page-views and ${res.data?.outbound_clicks_deleted ?? 0} outbound clicks`);
      qc.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("analytics-") });
    } catch (e) {
      toast.error("Failed to clear preview traffic");
    } finally {
      setBusy(false);
    }
  }
  return (
    <DangerButton onClick={onClick} disabled={busy} data-testid="analytics-clear-preview" className="border-amber/40 text-amber hover:bg-amber/5">
      {busy ? "Clearing…" : "Clear preview traffic"}
    </DangerButton>
  );
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}
function fmtAbs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
}

function Kpi({ label, value, testid }) {
  return (
    <Card>
      <div className="text-xs uppercase tracking-wider text-[#6C757D]">{label}</div>
      <div className="text-3xl font-semibold mt-1" data-testid={testid}>{value ?? "—"}</div>
    </Card>
  );
}

function OutboundTab({ start, end, country, includeOwner }) {
  const params = { start, end, country, include_owner: includeOwner };
  const summary = useQuery({
    queryKey: ["outbound-summary", params],
    queryFn: async () => (await api.get("/admin/analytics/outbound-summary", { params })).data,
  });
  const daily = useQuery({
    queryKey: ["outbound-daily", params],
    queryFn: async () => (await api.get("/admin/analytics/outbound-daily", { params })).data,
  });
  const byBrand = useQuery({
    queryKey: ["outbound-by-brand", params],
    queryFn: async () => (await api.get("/admin/analytics/outbound-by-brand", { params: { ...params, limit: 30 } })).data,
  });
  const byClothing = useQuery({
    queryKey: ["outbound-by-clothing", params],
    queryFn: async () => (await api.get("/admin/analytics/outbound-by-clothing", { params: { ...params, limit: 30 } })).data,
  });

  const s = summary.data || {};
  const trend = daily.data?.daily || [];
  const brands = byBrand.data?.brands || [];
  const items = byClothing.data?.items || [];

  return (
    <div className="space-y-6" data-testid="outbound-tab">
      <p className="text-xs text-[#6C757D] -mt-2">
        Tracks clicks on <strong>Visit website</strong> (brand pages) and <strong>View on brand</strong> (clothing pages) — i.e. the visitor leaving the site for the brand&apos;s own URL. Use it to measure how often editorial converts to a brand-site referral.
      </p>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Total clicks" value={s.total_clicks} testid="outbound-total-clicks" />
        <Kpi label="Unique visitors who clicked" value={s.unique_visitors} testid="outbound-unique-visitors" />
        <Kpi label="From brand pages" value={s.from_brand_page} testid="outbound-from-brand" />
        <Kpi label="From clothing pages" value={s.from_clothing_page} testid="outbound-from-clothing" />
      </div>

      {/* Daily trend */}
      <Card>
        <h3 className="font-semibold mb-3">Outbound clicks per day</h3>
        {trend.length === 0 ? (
          <div className="text-sm text-[#6C757D] py-8 text-center">No outbound clicks in this range yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E9ECEF" />
              <XAxis dataKey="date" stroke="#6C757D" fontSize={11} />
              <YAxis stroke="#6C757D" fontSize={11} allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="clicks" stroke="#1E5631" strokeWidth={2} dot={false} name="Clicks" />
              <Line type="monotone" dataKey="unique_visitors" stroke="#7B61FF" strokeWidth={2} dot={false} name="Unique visitors" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* By brand */}
      <Card>
        <h3 className="font-semibold mb-3">By destination brand</h3>
        {brands.length === 0 ? (
          <div className="text-sm text-[#6C757D] py-8 text-center">No data yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="outbound-by-brand-table">
            <thead>
              <tr className="text-left text-[#6C757D] text-xs">
                <th>Brand</th>
                <th className="text-right">Total clicks</th>
                <th className="text-right">From brand page</th>
                <th className="text-right">From clothing pages</th>
                <th className="text-right">Unique visitors</th>
              </tr>
            </thead>
            <tbody>
              {brands.map((b) => (
                <tr key={b.brand_id} className="border-t border-[#F1F3F5]">
                  <td className="py-2">
                    {b.brand_slug ? (
                      <a href={`/brands/${b.brand_slug}`} target="_blank" rel="noopener noreferrer" className="text-forest hover:underline">{b.brand_name || b.brand_slug}</a>
                    ) : (b.brand_name || "—")}
                  </td>
                  <td className="py-2 text-right font-medium">{b.clicks}</td>
                  <td className="py-2 text-right text-[#6C757D]">{b.from_brand_page}</td>
                  <td className="py-2 text-right text-[#6C757D]">{b.from_clothing_page}</td>
                  <td className="py-2 text-right text-[#6C757D]">{b.unique_visitors}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* By clothing item */}
      <Card>
        <h3 className="font-semibold mb-3">By clothing item</h3>
        {items.length === 0 ? (
          <div className="text-sm text-[#6C757D] py-8 text-center">No data yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="outbound-by-clothing-table">
            <thead>
              <tr className="text-left text-[#6C757D] text-xs">
                <th>Item</th>
                <th>Brand</th>
                <th className="text-right">Clicks</th>
                <th className="text-right">Unique visitors</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.target_id} className="border-t border-[#F1F3F5]">
                  <td className="py-2">
                    {it.slug ? (
                      <a href={`/clothing/${it.slug}`} target="_blank" rel="noopener noreferrer" className="text-forest hover:underline">{it.name}</a>
                    ) : (it.name || "—")}
                  </td>
                  <td className="py-2 text-[#6C757D]">{it.brand_name || "—"}</td>
                  <td className="py-2 text-right font-medium">{it.clicks}</td>
                  <td className="py-2 text-right text-[#6C757D]">{it.unique_visitors}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

